import { AxiosError } from 'axios';
import {
  AuthUserInfo,
  Customer,
  CustomerAbilityDefinition,
  CustomerAbilityDefinitionTable,
  CustomerResponse,
  ExpertiseCarBodyTypes,
  ExpertisePackageAbilityDefinitionType,
} from 'types';
import { localStorageUserAuthInfoKey } from './consts';

export const updateLocalStorageAuthInfo = (authUserInfo: AuthUserInfo) => {
  localStorage.setItem(localStorageUserAuthInfoKey, JSON.stringify(authUserInfo));
};

export const getExpertiseBackroundImageUrl = (expertiseCarBodyTypes: ExpertiseCarBodyTypes) => {
  const baseUrl = '/images/arac_modeller';
  let image = 'sedan.svg';
  switch (expertiseCarBodyTypes) {
    case ExpertiseCarBodyTypes.CanliPanelvan:
      image = 'Camli_Van.svg';
      break;
    case ExpertiseCarBodyTypes.Hatchback2:
      image = 'Hatchback_2_Kapi.svg';
      break;
    case ExpertiseCarBodyTypes.Hatchback5:
      image = 'Hatchback_4_Kapi.svg';
      break;
    case ExpertiseCarBodyTypes.Minibus:
      image = 'minibus.svg';
      break;
    case ExpertiseCarBodyTypes.TekSurguPanelvan:
      image = 'Panelvan_Tek_Surgu.svg';
      break;
    case ExpertiseCarBodyTypes.Panelvan:
      image = 'Panelvan.svg';
      break;
    case ExpertiseCarBodyTypes.Pickup:
      image = 'Pickup_4_Kapi.svg';
      break;
    case ExpertiseCarBodyTypes.Pickup2:
      image = 'Pickup_2_Kapi.svg';
      break;
    case ExpertiseCarBodyTypes.SUV:
      image = 'suv.svg';
      break;
    case ExpertiseCarBodyTypes.Station:
      image = 'Station_Wagon.svg';
      break;
    case ExpertiseCarBodyTypes.Sedan:
    default:
      image = 'sedan.svg';
      break;
  }

  return `${baseUrl}/${image}`;
};

export const vehicleBodyTypeIdToString = (vehicleTypeId: number): ExpertiseCarBodyTypes => {
  switch (vehicleTypeId) {
    case 2:
      return ExpertiseCarBodyTypes.Hatchback5;
    case 3:
      return ExpertiseCarBodyTypes.Hatchback2;
    case 4:
      return ExpertiseCarBodyTypes.Pickup;
    case 5:
      return ExpertiseCarBodyTypes.Pickup2;
    case 6:
      return ExpertiseCarBodyTypes.Minibus;
    case 7:
      return ExpertiseCarBodyTypes.Panelvan;
    case 8:
      return ExpertiseCarBodyTypes.CanliPanelvan;
    case 9:
      return ExpertiseCarBodyTypes.TekSurguPanelvan;
    case 1:
    default:
      return ExpertiseCarBodyTypes.Sedan;
  }
};

export const createEmptyCustomerAbilityTable = () => {
  return Object.values(ExpertisePackageAbilityDefinitionType)
    .filter((v) => !Number.isNaN(Number(v)))
    .reduce((accum: any, next: string | ExpertisePackageAbilityDefinitionType) => {
      // eslint-disable-next-line no-param-reassign
      accum[next] = false;
      return accum;
    }, {});
};
export const createCustomerAbilityTable = (abilities: CustomerAbilityDefinition[]): CustomerAbilityDefinitionTable => {
  const initialValues = createEmptyCustomerAbilityTable();
  return abilities.reduce((accum: any, next: CustomerAbilityDefinition) => {
    // eslint-disable-next-line no-param-reassign
    accum[next.abilityDefinitionType] = next.hasAbility;
    return accum;
  }, initialValues);
};

export const yupNameFieldCheck = (name: string | undefined): boolean => {
  if (!name) {
    return false;
  }
  const splitSentence = name.split(' ');
  if (splitSentence.some(({ length }) => length <= 1)) {
    return false;
  }
  return true;
};

export const yupNameFieldWhiteSpaceCheck = (name: string | undefined): boolean => {
  if (!name) {
    return false;
  }
  if (name.includes('  ')) {
    return false;
  }
  const splitSentence = name.split(' ');
  if (splitSentence[0] === '' || splitSentence[splitSentence.length - 1] === '') {
    return false;
  }
  return true;
};

export const yupGovIdFirstCharCheck = (name: string | undefined): boolean => {
  if (name?.startsWith('0')) {
    return false;
  }
  return true;
};

export const customerResponseToCustomer = (customerResponse: CustomerResponse): Customer => {
  const { customer, hasKvkkApproval, hasIsemriApproval, canContinue, displayPlate, updateUserInfo } = customerResponse;
  customer.hasKvkkApproval = hasKvkkApproval;
  customer.hasIsemriApproval = hasIsemriApproval;
  customer.canContinue = canContinue;
  customer.displayPlate = displayPlate;
  customer.updateUserInfo = updateUserInfo;
  return customer;
};

export const getRandomColorForChartPie = (dataSize: number) => {
  // generates random colours and puts them in string
  const colors = [];
  for (let i = 0; i < dataSize; i += 1) {
    const letters = '0123456789ABCDEF'.split('');
    let color = '#';
    for (let x = 0; x < 6; x += 1) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    colors.push(color);
  }
  return colors;
};

export const getErrorMessage = (error: Error, defaultError: string): string => {
  if (error instanceof AxiosError) {
    if (error.response) {
      if (error.response.data) {
        if (error.response.data.message) {
          return error.response.data.message;
        }
      }
    }
  }

  return defaultError;
};

export const toTitleCase = (text: string): string => {
  const titleCase = text
    .toLowerCase()
    .split(' ')
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');

  return titleCase;
};

export const downloadBlobAsFile = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
};

/**
 * Determines the brand name based on the current domain
 * @returns The brand name to display in page titles
 */
export const getBrandName = (): string => {
  const { hostname } = window.location;

  // Check if it's Auto Kale domain (staging or production)
  if (
    hostname.includes('autokale.com') ||
    hostname.includes('bayi.autokale.com') ||
    hostname.includes('staging.bayi.autokale.com')
  ) {
    return 'AUTO KALE';
  }

  // Default to OCCO for other domains (like ekspertiz.occocar.com)
  return 'OCCO EKSPERTİZ';
};

/**
 * Sets the page title with the appropriate brand name and optional page title
 * @param pageTitle Optional page title to append after the brand name
 */
export const setPageTitle = (pageTitle?: string): void => {
  const brandName = getBrandName();
  const title = pageTitle ? `${brandName} | ${pageTitle}` : brandName;
  document.title = title;
};

/**
 * Opens an image URL in a new tab formatted for A4 printing.
 * The image is centered on the page and optimized for print output.
 * @param imageUrl The URL of the image to print
 * @param title The title to display in the browser tab (default: "Yazdır")
 */
export const openImageForPrint = (imageUrl: string, title = 'Yazdır'): void => {
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${title}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        @page {
          size: A4;
          margin: 10mm;
        }
        
        body {
          display: flex;
          justify-content: center;
          align-items: flex-start;
          min-height: 100vh;
          background: #f0f0f0;
          padding: 20px;
        }
        
        .print-container {
          background: white;
          padding: 10mm;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          max-width: 210mm;
        }
        
        .print-container img {
          max-width: 100%;
          height: auto;
          display: block;
        }
        
        @media print {
          body {
            background: white;
            padding: 0;
          }
          
          .print-container {
            box-shadow: none;
            padding: 0;
            max-width: 100%;
          }
          
          .print-container img {
            max-width: 100%;
            max-height: 100vh;
            object-fit: contain;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-container">
        <img src="${imageUrl}" alt="Rapor" onload="window.print();" />
      </div>
    </body>
    </html>
  `;

  const blob = new Blob([htmlContent], { type: 'text/html' });
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, '_blank');
};
